/**
 * @spec docs/superpowers/specs/2026-10-13-wallet-network-awareness-design.md §4.1
 * Live chain of one wallet, for every wallet kind (the Privy embedded wallet included: it emits
 * chainChanged when it switches, spec §7.5). WalletProvider runs it for the selected wallet (one
 * app-wide subscription); useNetworkSwitch runs it only for a wallet other than the selected one
 * (AddressPage walletOverride). Pass null to opt out without breaking the rules of hooks.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ConnectedWallet } from '@privy-io/react-auth';
import { parseCaip2ChainId, parseChainIdHex } from '@/lib/network-gate';

export interface UseLiveChainIdReturn {
  /** Live chain, else Privy's connect-time cached chain, else null */
  chainId: number | null;
  /** Re-read eth_chainId, for a provider that does not emit chainChanged */
  refresh: () => Promise<number | null>;
}

export function useLiveChainId(wallet: ConnectedWallet | null): UseLiveChainIdReturn {
  // LIVE chain tracking. Privy's `wallet.chainId` is a connect-time snapshot: an external EOA
  // (MetaMask etc.) can switch networks afterwards and the cached value goes stale, so the
  // wrong-network CTA never rendered and users only discovered the mismatch at submit time
  // ("Wrong network. Please switch to chain ID 8453" with no way to switch — 2026-08-31 report).
  // Read eth_chainId from the wallet's own provider and subscribe to chainChanged.
  const [liveChainId, setLiveChainId] = useState<number | null>(null);
  // Keyed on the ADDRESS, not the ConnectedWallet reference: Privy's useWallets can hand back a
  // fresh object for the same wallet on unrelated re-renders, which would churn this effect
  // (clearing liveChainId and re-requesting the provider) with no real wallet change.
  const walletRef = useRef(wallet);
  walletRef.current = wallet;
  const walletAddress = wallet?.address;

  useEffect(() => {
    let cancelled = false;
    let cleanup: (() => void) | undefined;
    setLiveChainId(null);
    const w = walletRef.current;
    if (!w || !walletAddress) return;
    void (async () => {
      try {
        const provider = await w.getEthereumProvider();
        const reply = await provider.request({ method: 'eth_chainId' });
        // Re-check cancellation after EVERY await: if the wallet changed (or we unmounted) while
        // getEthereumProvider()/request() were pending, registering the OLD provider's listener
        // here would outlive cleanup and overwrite liveChainId for the NEW wallet.
        if (cancelled) return;
        setLiveChainId(parseChainIdHex(reply));
        const onChainChanged = (newHex: unknown) => {
          setLiveChainId(parseChainIdHex(newHex));
        };
        const p = provider as unknown as {
          on?: (event: string, cb: (arg: unknown) => void) => void;
          removeListener?: (event: string, cb: (arg: unknown) => void) => void;
        };
        p.on?.('chainChanged', onChainChanged);
        if (cancelled) {
          // cancellation raced the registration above — detach immediately.
          p.removeListener?.('chainChanged', onChainChanged);
          return;
        }
        cleanup = () => p.removeListener?.('chainChanged', onChainChanged);
      } catch {
        // Provider unavailable: fall back to Privy's cached value below.
      }
    })();
    return () => {
      cancelled = true;
      cleanup?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- walletRef tracks the latest
    // object; the address is the identity that should retrigger the subscription.
  }, [walletAddress]);

  const refresh = useCallback(async (): Promise<number | null> => {
    const w = walletRef.current;
    if (!w) return null;
    const address = w.address;
    try {
      const provider = await w.getEthereumProvider();
      const next = parseChainIdHex(await provider.request({ method: 'eth_chainId' }));
      // The read belongs to the wallet that started it; a late reply must not write into
      // a newly selected wallet's chain.
      if (walletRef.current?.address !== address) return null;
      setLiveChainId(next);
      return next;
    } catch {
      return null;
    }
  }, []);

  return { chainId: liveChainId ?? parseCaip2ChainId(wallet?.chainId), refresh };
}
